// src/api/resourceService.js
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_API_BASE_URL}/metadata/resources`;

// The login token is sent by the axios defaults set in authService, so submitters and
// reviewers also see pending resources.

/**
 * Resource types, knowledge types and the endorsement rule.
 */
export const getResourceOptions = async () => {
  const response = await axios.get(`${API_URL}/options`);
  return response.data;
};

/**
 * Share a file or link.
 * @param {FormData} formData - resource_type, knowledge_type, title, description, usage_guide,
 *   and url (links) or file (uploads). Optional: tags (comma-separated), license,
 *   surah_id, ayah_start, ayah_end, reciter_name
 * @param {Function} onUploadProgress - optional callback receiving a 0-100 percentage
 */
export const createResource = async (formData, onUploadProgress) => {
  const response = await axios.post(API_URL, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onUploadProgress
      ? (event) => event.total && onUploadProgress(Math.round((event.loaded * 100) / event.total))
      : undefined,
  });
  return response.data;
};

/**
 * List resources. params: search, resource_type, knowledge_type, status,
 * view ('all' | 'mine' | 'review'), sort ('top' | 'newest'), page, page_size
 */
export const getResources = async (params = {}) => {
  const response = await axios.get(API_URL, { params });
  return response.data;
};

/**
 * Get a resource with every expert's vote.
 */
export const getResource = async (id) => {
  const response = await axios.get(`${API_URL}/${id}`);
  return response.data;
};

/**
 * Edit a resource's details (submitter or admin).
 */
export const updateResource = async (id, changes) => {
  const response = await axios.put(`${API_URL}/${id}`, changes);
  return response.data;
};

export const deleteResource = async (id) => {
  const response = await axios.delete(`${API_URL}/${id}`);
  return response.data;
};

/**
 * Cast or change a vote (experts, verifiers and admins).
 * @param {'endorse'|'not_endorse'} vote
 */
export const endorseResource = async (id, vote, comment) => {
  const response = await axios.put(`${API_URL}/${id}/endorsement`, { vote, comment });
  return response.data;
};

export const withdrawEndorsement = async (id) => {
  const response = await axios.delete(`${API_URL}/${id}/endorsement`);
  return response.data;
};

/**
 * Address that shows an image, PDF or audio file in the page (not counted as a download).
 * Usable directly in <img>/<audio>/<iframe> only for published resources, since those
 * requests can't carry the login token.
 */
export const getResourcePreviewUrl = (id) => `${API_URL}/${id}/preview`;

/**
 * Fetch a preview with the login token, for resources that are still pending.
 * @returns {Promise<Blob>}
 */
export const getResourcePreviewBlob = async (id) => {
  const response = await axios.get(getResourcePreviewUrl(id), { responseType: 'blob' });
  return response.data;
};

/**
 * A preview returned as data rather than the file itself:
 * - text/table (CSV, JSON, TXT): {text, truncated} with the start of the file
 * - sheet (XLSX): {sheets: [{name, rows, truncated}], more_sheets}
 * - document (DOCX): {html, truncated}
 * - archive (ZIP): {entries: [{name, size, is_dir}], total_entries, total_files, total_size}
 */
export const getResourcePreviewData = async (id) => {
  const response = await axios.get(getResourcePreviewUrl(id));
  return response.data;
};

/**
 * Download a resource's file. Fetched as a blob so the login token is sent
 * (needed for the submitter or a reviewer to download a resource that is still pending).
 */
export const downloadResource = async (id, fallbackName = 'resource') => {
  const response = await axios.get(`${API_URL}/${id}/download`, {
    responseType: 'blob',
  });

  const disposition = response.headers['content-disposition'] || '';
  const match = /filename="?([^";]+)"?/.exec(disposition);
  const filename = match ? match[1] : fallbackName;

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
